import React, { useState, useEffect, useRef, useMemo } from 'react';
import Fuse from 'fuse.js';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme as useMuiTheme
} from '@mui/material';
import { PrimaryButton, AppIconButton } from './ui';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  ShoppingBagIcon
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../config/currency';
import api from '../api/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import LoadingScreen from './LoadingScreen';
import StandalonePortionSelector from './StandalonePortionSelector';
import { SpiceLevel } from '../types/menu';
import { CURRENCY } from '../config/currency';

interface Portion {
  id: number;
  portionName: string;
  effectivePrice: number;
  isDefault: boolean;
}

interface MenuItem {
  id: number;
  name: string;
  description?: string;
  price: number;
  isAvailable: boolean;
  categoryId?: number;
  categoryName?: string;
  portions?: Portion[];
  spiceLevel?: SpiceLevel;
  allowsSpiceCustomization?: boolean;
}

interface MenuCategory {
  id: number;
  name: string;
  vendorId: number;
}

interface CartItem {
  menuItemId: number;
  menuItemName: string;
  portionId?: number;
  portionName?: string;
  quantity: number;
  price: number;
  specialInstructions?: string;
  requestedSpiceLevel?: SpiceLevel;
}

interface AddItemsToOrderModalProps {
  open: boolean;
  onClose: () => void;
  onAddItems: (items: CartItem[]) => void;
  vendorId?: number;
  tableId?: number;
}

const AddItemsToOrderModal: React.FC<AddItemsToOrderModalProps> = ({
  open,
  onClose,
  onAddItems,
  vendorId,
  tableId
}) => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [menuCategories, setMenuCategories] = useState<MenuCategory[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState<Map<string, CartItem>>(new Map());
  const [selectedPortions, setSelectedPortions] = useState<{ [itemId: number]: number }>({});
  const [selectedSpiceLevels, setSelectedSpiceLevels] = useState<{ [itemId: number]: SpiceLevel }>({});
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);

  // Debug log for mobile detection
  useEffect(() => {
    console.log('AddItemsToOrderModal - isMobile:', isMobile, 'window.innerWidth:', window.innerWidth);
  }, [isMobile]);

  useEffect(() => {
    if (open) {
      console.log('AddItemsToOrderModal opened with tableId:', tableId, 'vendorId:', vendorId);
      if (tableId) {
        // If tableId is provided, use table-specific menu
        fetchMenu();
      } else if (vendorId) {
        fetchMenu();
      } else if (user?.vendorId) {
        // Try to get vendor ID from user context
        fetchMenu(user.vendorId);
      }
    }
  }, [open, vendorId, tableId, user]);

  const fetchMenu = async (vId?: number) => {
    try {
      setLoading(true);
      
      // Use the same endpoints as ImprovedVendorOrderFormModal
      let response;
      try {
        // Only try table-specific menu if tableId is numeric (actual table ID)
        // Skip for special values like "TAKEAWAY", "DINE_IN", etc.
        const isNumericTableId = tableId && /^\d+$/.test(String(tableId));

        if (isNumericTableId) {
          // Try table-specific menu first if tableId is a valid numeric ID
          try {
            response = await api.get(`/api/menu/table/${tableId}`);
            console.log('Loaded table-specific menu for table:', tableId);
          } catch (tableMenuError: any) {
            console.error('Table-specific menu failed:', tableMenuError.response?.data);
            console.log('Falling back to general menu');
            response = await api.get('/api/menu');
          }
        } else {
          // Use general menu endpoint for non-numeric tableIds (TAKEAWAY, etc.)
          response = await api.get('/api/menu');
          console.log('Loaded general menu');
      }
    } catch (err) {
      console.error('Failed to load menu:', err);
      toast.error('Failed to load menu');
      setLoading(false);
      return;
    }
      
      const categoriesWithItems = response.data || [];
      
      // Extract categories and flatten items
      const categories: MenuCategory[] = [];
      const allItems: MenuItem[] = [];
      
      categoriesWithItems.forEach((category: any) => {
        categories.push({
          id: category.id,
          name: category.name,
          vendorId: category.vendor?.id || vId || vendorId || user?.vendorId || 0
        });
        
        if (category.items) {
          category.items.forEach((item: any) => {
            allItems.push({
              ...item,
              categoryId: category.id,
              categoryName: category.name
            });
          });
        }
      });
      
      setMenuCategories(categories);
      setMenuItems(allItems);
      
      if (categories.length > 0) {
        setSelectedCategory(categories[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch menu:', error);
      toast.error('Failed to load menu');
    } finally {
      setLoading(false);
    }
  };

  const getCartKey = (itemId: number, portionId?: number) => {
    return portionId ? `${itemId}-${portionId}` : `${itemId}`;
  };

  const handleAddToCart = (item: MenuItem) => {
    const portionId = selectedPortions[item.id];
    const portion = item.portions?.find(p => p.id === portionId) ||
                   item.portions?.find(p => p.isDefault) ||
                   item.portions?.[0];

    const price = portion ? portion.effectivePrice : item.price;
    const key = getCartKey(item.id, portionId);
    const requestedSpiceLevel = selectedSpiceLevels[item.id] || item.spiceLevel;

    const currentItem = cart.get(key);
    if (currentItem) {
      const updatedCart = new Map(cart);
      updatedCart.set(key, {
        ...currentItem,
        quantity: currentItem.quantity + 1
      });
      setCart(updatedCart);
    } else {
      const newCart = new Map(cart);
      newCart.set(key, {
        menuItemId: item.id,
        menuItemName: item.name,
        portionId: portion?.id,
        portionName: portion?.portionName,
        quantity: 1,
        price,
        requestedSpiceLevel: item.allowsSpiceCustomization ? requestedSpiceLevel : undefined
      });
      setCart(newCart);
    }
  };

  const handleRemoveFromCart = (item: MenuItem) => {
    const portionId = selectedPortions[item.id];
    const key = getCartKey(item.id, portionId);
    
    const currentItem = cart.get(key);
    if (currentItem && currentItem.quantity > 1) {
      const updatedCart = new Map(cart);
      updatedCart.set(key, {
        ...currentItem,
        quantity: currentItem.quantity - 1
      });
      setCart(updatedCart);
    } else {
      const newCart = new Map(cart);
      newCart.delete(key);
      setCart(newCart);
    }
  };

  const updateQuantity = (itemId: number, portionId: number | undefined, newQuantity: number) => {
    const key = getCartKey(itemId, portionId);
    
    if (newQuantity <= 0) {
      const newCart = new Map(cart);
      newCart.delete(key);
      setCart(newCart);
    } else {
      const currentItem = cart.get(key);
      if (currentItem) {
        const updatedCart = new Map(cart);
        updatedCart.set(key, {
          ...currentItem,
          quantity: newQuantity
        });
        setCart(updatedCart);
      }
    }
  };

  const getItemQuantity = (itemId: number) => {
    // Sum all quantities for this item across all portions
    let total = 0;
    cart.forEach((cartItem) => {
      if (cartItem.menuItemId === itemId) {
        total += cartItem.quantity;
      }
    });
    return total;
  };

  const getItemPortionBreakdown = (itemId: number) => {
    // Get portion breakdown like "1S, 2M" for display
    const portionCounts: { [key: string]: number } = {};
    cart.forEach((cartItem) => {
      if (cartItem.menuItemId === itemId && cartItem.portionName) {
        const shortName = cartItem.portionName.substring(0, 1); // First letter
        portionCounts[shortName] = (portionCounts[shortName] || 0) + cartItem.quantity;
      }
    });
    return Object.entries(portionCounts)
      .map(([name, count]) => `${count}${name}`)
      .join(', ');
  };

  const handlePortionSelections = (itemId: number, selections: Array<{ portionId?: number; portionName?: string; quantity: number; price: number; spiceLevel?: SpiceLevel }>) => {
    const item = menuItems.find(i => i.id === itemId);
    if (!item) return;

    // Clear existing cart entries for this item
    const newCart = new Map(cart);
    Array.from(newCart.keys()).forEach(key => {
      const cartItem = newCart.get(key);
      if (cartItem && cartItem.menuItemId === itemId) {
        newCart.delete(key);
      }
    });

    // Add new selections
    selections.forEach(selection => {
      const key = getCartKey(itemId, selection.portionId);
      newCart.set(key, {
        menuItemId: itemId,
        menuItemName: item.name,
        portionId: selection.portionId,
        portionName: selection.portionName,
        quantity: selection.quantity,
        price: selection.price,
        requestedSpiceLevel: selection.spiceLevel
      });
    });

    setCart(newCart);
    
    // Close the dropdown modal
    setOpenDropdown(null);
  };

  const getExistingQuantities = (itemId: number): { [portionId: number]: number } => {
    const quantities: { [portionId: number]: number } = {};
    cart.forEach((cartItem) => {
      if (cartItem.menuItemId === itemId && cartItem.portionId) {
        quantities[cartItem.portionId] = cartItem.quantity;
      }
    });
    return quantities;
  };

  const handleAddToOrder = () => {
    const items = Array.from(cart.values());
    onAddItems(items);
    setCart(new Map());
    onClose();
  };

  const getTotalItems = () => {
    return Array.from(cart.values()).reduce((sum, item) => sum + item.quantity, 0);
  };

  const getTotalAmount = () => {
    return Array.from(cart.values()).reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  // Fuzzy search configuration
  const fuseOptions = useMemo(() => ({
    keys: [
      { name: 'name', weight: 0.7 },
      { name: 'description', weight: 0.3 }
    ],
    threshold: 0.4,
    distance: 100,
    minMatchCharLength: 2,
    includeScore: true,
    ignoreLocation: true,
    useExtendedSearch: false
  }), []);

  // Create Fuse instance
  const fuse = useMemo(() => new Fuse(menuItems, fuseOptions), [menuItems, fuseOptions]);

  const filteredItems = useMemo(() => {
    if (searchQuery && searchQuery.length >= 2) {
      // Use fuzzy search
      const results = fuse.search(searchQuery);
      return results.map(result => result.item);
    }
    if (selectedCategory) {
      return menuItems.filter(item => item.categoryId === selectedCategory);
    }
    return [];
  }, [searchQuery, selectedCategory, menuItems, fuse]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile} // Use fullscreen on mobile
      PaperProps={{
        sx: {
          height: isMobile ? '100vh' : '90vh',
          display: 'flex',
          flexDirection: 'column',
          margin: isMobile ? 0 : undefined,
          backgroundColor: theme.background || '#ffffff',
          // Add safe area support for mobile devices with !important to override MUI defaults
          ...(isMobile && {
            paddingTop: 'max(20px, env(safe-area-inset-top)) !important',
            paddingBottom: 'max(20px, env(safe-area-inset-bottom)) !important',
            paddingLeft: 'max(0px, env(safe-area-inset-left)) !important',
            paddingRight: 'max(0px, env(safe-area-inset-right)) !important',
            boxSizing: 'border-box'
          })
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: 1, 
        borderColor: 'divider',
        background: theme.background,
        color: theme.text,
        p: { xs: 2, sm: 2.5 },
        pt: { xs: 2, sm: 2.5 },
        flexShrink: 0,
        position: 'relative'
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            sx={{ 
              fontWeight: 600,
              color: 'white',
              pr: isMobile ? 6 : 0
            }}
          >
            Add Items to Order
          </Typography>
          <AppIconButton
            icon={<XMarkIcon style={{ width: 24, height: 24, strokeWidth: 2, color: theme.primary }} />}
            tooltip="Close"
            variant="ghost"
            size="small"
            onClick={onClose}
            sx={{
              position: isMobile ? "absolute" : "relative",
              top: isMobile ? 12 : 0,
              right: isMobile ? 12 : 0,
              minWidth: '32px !important',
              minHeight: '32px !important',
              width: '32px !important',
              height: '32px !important',
              p: '0 !important',
              color: `${theme.primary}`,
              backgroundColor: 'transparent !important',
              border: 'none !important',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: `${theme.primary}08 !important`,
                transform: 'scale(1.05)'
              }
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
        {loading ? (
          <Box py={4}>
            <LoadingScreen message="Loading menu..." />
          </Box>
        ) : (
          <>
            <Box 
              display="flex" 
              flex={1} 
              height="100%"
              flexDirection={isMobile ? 'column' : 'row'}
              sx={{ overflow: 'hidden' }}
            >
            {/* Categories Section */}
            <Box 
              sx={{ 
                width: isMobile ? '100%' : 200,
                minHeight: isMobile ? 'auto' : '100%',
                borderRight: isMobile ? 0 : 1,
                borderBottom: isMobile ? 1 : 0,
                borderColor: 'divider',
                backgroundColor: 'grey.50',
                maxHeight: isMobile ? 'auto' : 'none', // Auto height on mobile
                overflow: 'auto',
                flexShrink: 0 // Prevent shrinking
              }}
            >
              <Box p={isMobile ? 1 : 2}>
                <TextField
                  fullWidth
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MagnifyingGlassIcon style={{ width: 20, height: 20 }} />
                      </InputAdornment>
                    ),
                    sx: {
                      ...(isMobile && {
                        fontSize: '0.875rem', // Smaller text on mobile
                        py: 0.5
                      })
                    }
                  }}
                />
              </Box>
              
              {!searchQuery && (
                <Box sx={{
                  display: 'flex !important',
                  flexDirection: isMobile ? 'row !important' : 'column', // Horizontal on mobile
                  gap: isMobile ? 1 : 0,
                  overflowX: isMobile ? 'auto' : 'visible',
                  pb: isMobile ? 1 : 0,
                  px: isMobile ? 1 : 0, // Horizontal padding on mobile
                  // Hide scrollbar on mobile
                  '&::-webkit-scrollbar': { display: isMobile ? 'none' : 'auto' },
                  msOverflowStyle: isMobile ? 'none' : 'auto',
                  scrollbarWidth: isMobile ? 'none' : 'auto'
                }}>
                  {menuCategories.map((category) => {
                    const isSelected = selectedCategory === category.id;
                    return (
                      <Box
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        sx={{
                          p: isMobile ? 1 : 2,
                          cursor: 'pointer',
                          backgroundColor: isSelected ? `${theme.primary}22 !important` : 'transparent',
                          color: isSelected ? `${theme.primary} !important` : 'text.primary',
                          border: isSelected ? `1px solid ${theme.primary} !important` : (isMobile ? '1px solid #e0e0e0' : 'none'),
                          borderRadius: isMobile ? '16px !important' : 0,
                          whiteSpace: 'nowrap !important',
                          flexShrink: '0 !important',
                          minWidth: isMobile ? 'auto' : 'unset',
                          boxShadow: 'none !important',
                          fontWeight: isSelected ? 600 : 400,
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: isSelected ? `${theme.primary}44 !important` : 'grey.100'
                          }
                        }}
                      >
                        <Typography 
                          variant={isMobile ? "caption" : "body2"} // Smaller text on mobile
                          sx={{
                            // Ensure text displays properly
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordBreak: 'normal',
                            fontWeight: isSelected ? 600 : 400
                          }}
                        >
                          {category.name}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Menu Items */}
            <Box 
              flex={1} 
              p={isMobile ? 1.5 : 2} 
              sx={{
                overflowY: 'auto',
                height: isMobile ? 'auto' : '100%',
                minHeight: 0,
                // Add bottom padding when cart has items to prevent content being hidden
                pb: cart.size > 0 ? (isMobile ? 10 : 2) : (isMobile ? 1.5 : 2)
              }}
            >
              {filteredItems.map((item) => {
                const quantity = getItemQuantity(item.id);
                const selectedPortion = item.portions?.find(p => p.isDefault) || item.portions?.[0];
                const price = selectedPortion ? selectedPortion.effectivePrice : item.price;

                return (
                  <Box
                    key={item.id}
                    sx={{
                      p: isMobile ? 1.5 : 2,
                      mb: 1,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'background.paper'
                    }}
                  >
                    <Box 
                      display="flex" 
                      justifyContent="space-between" 
                      alignItems="start"
                      gap={2}
                    >
                      <Box flex={1} sx={{ minWidth: 0 }}>
                        <Typography 
                          variant="subtitle1" 
                          fontWeight={500}
                          sx={{
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: 1.3
                          }}
                        >
                          {item.name}
                        </Typography>
                        {item.description && (
                          <Typography 
                            variant="body2" 
                            color="textSecondary" 
                            sx={{ 
                              mt: 0.5,
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                              lineHeight: 1.4,
                              fontSize: '0.875rem',
                              color: 'var(--text-tertiary)'
                            }}
                          >
                            {item.description}
                          </Typography>
                        )}
                        
                        {/* Display portion options directly on card like customer menu */}
                        {item.portions && item.portions.length > 1 && (
                          <Box mt={1}>
                            <Box display="flex" flexWrap="wrap" gap={0.75}>
                              {item.portions.map((portion) => (
                                <Box
                                  key={portion.id}
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'baseline',
                                    gap: 0.75,
                                    px: 1.25,
                                    py: 0.5,
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    backgroundColor: 'rgba(241, 245, 249, 1)',
                                    border: '1px solid rgba(203, 213, 225, 0.6)',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>
                                    {portion.portionName}
                                  </span>
                                  <span style={{ fontWeight: 700, color: 'var(--text-link)' }}>
                                    {CURRENCY.symbol}{portion.effectivePrice.toFixed(0)}
                                  </span>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}

                        <Typography variant="h6" sx={{ mt: 1.5, fontWeight: 700, fontSize: '1.125rem', color: 'var(--text-primary)', mb: 1 }}>
                          {CURRENCY.symbol}{price.toFixed(2)}
                        </Typography>

                        {/* Availability status */}
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              px: 1.25,
                              py: 0.5,
                              borderRadius: '6px',
                              color: item.isAvailable ? '#059669' : '#dc2626',
                              backgroundColor: item.isAvailable ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                              border: item.isAvailable ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)',
                              whiteSpace: 'nowrap',
                              display: 'inline-flex',
                              alignItems: 'center'
                            }}
                          >
                            {item.isAvailable ? 'Available' : 'Unavailable'}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Right side - Add/Edit Button */}
                      <Box 
                        display="flex" 
                        flexDirection="column"
                        alignItems="center"
                        gap={1}
                        sx={{ flexShrink: 0 }}
                      >
                        {quantity > 0 ? (
                          // Show Edit button if item is in cart
                          item.portions && item.portions.length > 1 ? (
                            // Multi-portion item - show count and Edit button
                            <Box display="flex" alignItems="center" gap={0.75} sx={{ backgroundColor: 'var(--bg-sunken)', px: 1, py: 0.5, borderRadius: '8px' }}>
                              <Box display="flex" flexDirection="column" alignItems="center" sx={{ minWidth: { xs: '40px', sm: '50px' } }}>
                                <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.8125rem', sm: '0.875rem' }, color: 'var(--text-primary)', lineHeight: 1.2 }}>
                                  {quantity}
                                </Typography>
                                <Typography sx={{ fontSize: { xs: '0.6875rem', sm: '0.75rem' }, color: 'var(--text-tertiary)', lineHeight: 1.2 }}>
                                  {getItemPortionBreakdown(item.id)}
                                </Typography>
                              </Box>
                              <button
                                onClick={() => setOpenDropdown(item.id)}
                                style={{
                                  backgroundColor: '#1e293b', /* basil-todo */
                                  color: 'var(--text-inverse)',
                                  border: '1px solid #334155', /* basil-todo */
                                  padding: window.innerWidth < 640 ? '5px 10px' : '7px 14px',
                                  borderRadius: '6px',
                                  fontSize: window.innerWidth < 640 ? '0.75rem' : '0.8125rem',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  whiteSpace: 'nowrap',
                                  height: 'fit-content'
                                }}
                              >
                                Edit
                              </button>
                            </Box>
                          ) : (
                            // Simple item - show quantity stepper (no Edit button)
                            <Box display="flex" alignItems="center" gap={1} sx={{ backgroundColor: 'var(--bg-sunken)', px: 0.5, py: 0.5, borderRadius: '8px' }}>
                              <button
                                onClick={() => {
                                  const currentQty = quantity;
                                  if (currentQty > 0) {
                                    updateQuantity(item.id, undefined, currentQty - 1);
                                  }
                                }}
                                style={{
                                  backgroundColor: 'var(--bg-sunken)',
                                  color: 'var(--text-secondary)',
                                  border: 'none',
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '6px',
                                  fontSize: '1.25rem',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                −
                              </button>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-primary)', minWidth: '24px', textAlign: 'center' }}>
                                {quantity}
                              </Typography>
                              <button
                                onClick={() => {
                                  const currentQty = quantity;
                                  updateQuantity(item.id, undefined, currentQty + 1);
                                }}
                                style={{
                                  backgroundColor: '#1e293b', /* basil-todo */
                                  color: 'var(--text-inverse)',
                                  border: 'none',
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '6px',
                                  fontSize: '1.25rem',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                +
                              </button>
                            </Box>
                          )
                        ) : (
                          // Show Add button if item not in cart
                          <button
                            onClick={() => {
                              console.log('🔥 ADD BUTTON CLICKED!', item.name, 'Has portions:', item.portions?.length);
                              if (!item.isAvailable) return;
                              // Show dropdown if item has multiple portions OR allows spice customization
                              if ((item.portions && item.portions.length > 1) || item.allowsSpiceCustomization) {
                                console.log('🔥 OPENING DROPDOWN for item', item.id);
                                setOpenDropdown(item.id);
                              } else {
                                handleAddToCart(item);
                              }
                            }}
                            disabled={!item.isAvailable}
                            style={{
                              backgroundColor: item.isAvailable ? '#1e293b' : '#e5e7eb',
                              color: item.isAvailable ? '#ffffff' : '#9ca3af',
                              border: item.isAvailable ? '1px solid #334155' : '1px solid #d1d5db',
                              padding: '10px 16px',
                              borderRadius: '8px',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              cursor: item.isAvailable ? 'pointer' : 'not-allowed',
                              minWidth: '80px',
                              opacity: item.isAvailable ? 1 : 0.5,
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            Add {((item.portions && item.portions.length > 1) || item.allowsSpiceCustomization) ? '▼' : ''}
                          </button>
                        )}
                        
                        {/* Show item total if in cart */}
                        {quantity > 0 && (
                          <Typography sx={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', fontWeight: 500, textAlign: 'center' }}>
                            Total: {CURRENCY.symbol}{(() => {
                              let total = 0;
                              cart.forEach((cartItem) => {
                                if (cartItem.menuItemId === item.id) {
                                  total += cartItem.price * cartItem.quantity;
                                }
                              });
                              return total.toFixed(2);
                            })()}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

        {/* Standalone Portion Selector Modal - Rendered outside items loop */}
        {openDropdown !== null && (() => {
          console.log('🎯 MODAL RENDERING CHECK - openDropdown:', openDropdown);
          const selectedItem = menuItems.find(item => item.id === openDropdown);
          console.log('🎯 Found selectedItem:', selectedItem?.name, 'Has portions:', selectedItem?.portions?.length);
          if (selectedItem && selectedItem.portions && selectedItem.portions.length > 1) {
            console.log('✅ RENDERING StandalonePortionSelector');
            return (
              <StandalonePortionSelector
                item={{
                  id: selectedItem.id,
                  name: selectedItem.name,
                  portions: selectedItem.portions,
                  allowsSpiceCustomization: selectedItem.allowsSpiceCustomization,
                  spiceLevel: selectedItem.spiceLevel
                }}
                existingQuantities={getExistingQuantities(selectedItem.id)}
                onClose={() => setOpenDropdown(null)}
                onAddToCart={(selections) => handlePortionSelections(selectedItem.id, selections)}
              />
            );
          }
          console.log('❌ NOT RENDERING - conditions not met');
          return null;
        })()}

        {/* Cart Summary - Matches customer view layout */}
        {cart.size > 0 && (
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              background: theme.components?.card?.background || theme.background,
              color: theme.text,
              flexShrink: 0,
              position: 'relative',
              zIndex: 100,
              boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
              // Add bottom safe area padding on mobile
              ...(isMobile && {
                pb: 2.5
              })
            }}
          >
            <Box 
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2
              }}
            >
              {/* Left side - Item count and total */}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                  {getTotalItems()} {getTotalItems() === 1 ? 'item' : 'items'}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 600 }}>
                  Total: {formatCurrency(getTotalAmount())}
                </Typography>
              </Box>
              
              {/* Right side - Add to Order button */}
              <PrimaryButton
                onClick={handleAddToOrder}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  fontSize: isMobile ? '0.875rem' : undefined,
                  px: isMobile ? 2 : 3,
                  py: isMobile ? 0.75 : 1,
                  whiteSpace: 'nowrap',
                  boxShadow: 2,
                  flexShrink: 0,
                  ml: 'auto',
                }}
              >
                Add to Order
              </PrimaryButton>
            </Box>
          </Box>
        )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddItemsToOrderModal;